import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { AlertCircle, ExternalLink, ChevronRight } from 'react-feather';
import axios from 'axios';

import useOrgIdentifer from '@hooks/org_identifier';

// Routes where the actions banner should NOT render. Matches by path prefix.
const SKIP_PATH_PREFIXES = ['/admins', '/internal_dashboard', '/profile', '/server-error'];

const flattenActions = (raw) => {
  const out = [];
  (raw || []).forEach((n) => {
    if (n?.gst_update?.message) {
      out.push({
        message: n.gst_update.message,
        url: n.gst_update.url,
        label: 'Update GSTIN',
      });
    }
    if (n?.pending_feasibility_reports?.length > 0) {
      n.pending_feasibility_reports.forEach((r) => {
        out.push({
          message: r?.message,
          url: r?.url,
          label: 'View and Proceed',
        });
      });
    }
  });
  return out;
};

export default function ActionsBanner() {
  const org_identifier = useOrgIdentifer();
  const location = useLocation();
  const [actions, setActions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const skip =
    !org_identifier ||
    org_identifier === 'promptcloud' ||
    SKIP_PATH_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (skip) {
      setActions([]);
      return;
    }
    axios
      .get(`/api/v1/organizations/${org_identifier}/banner_notifications?page_type=client&controller_name=notifications`)
      .then((res) => setActions(flattenActions(res.data?.banner_notifications)))
      .catch(() => setActions([]));
  }, [org_identifier, skip]);

  if (skip || actions.length === 0) return null;

  const count = actions.length;
  const label = `${count} action${count > 1 ? 's' : ''} required`;
  const toggle = () => setModalOpen((v) => !v);

  return (
    <>
      <div
        className='d-flex align-items-center justify-content-between my-3 p-3 rounded'
        style={{
          background: '#fff8e1',
          border: '1px solid #ffd97a',
          color: '#5a4500',
          cursor: 'pointer',
        }}
        onClick={toggle}
        role='button'
        aria-label='View pending actions'
      >
        <div className='d-inline-flex align-items-center' style={{ minWidth: 0 }}>
          <AlertCircle size={20} className='me-2 flex-shrink-0' style={{ color: '#fd7e14' }} />
          <span style={{ fontSize: 14, fontWeight: 600 }} className='me-3'>
            {label}
          </span>
          <span className='text-truncate' style={{ fontSize: 13, opacity: 0.8 }}>
            Click to view and resolve
          </span>
        </div>
        <button
          type='button'
          className='btn btn-sm d-inline-flex align-items-center flex-shrink-0'
          onClick={(e) => { e.stopPropagation(); toggle(); }}
          style={{
            background: '#fd7e14',
            color: 'white',
            fontSize: 13,
            padding: '6px 14px',
            borderRadius: 6,
            fontWeight: 500,
            border: 'none',
          }}
        >
          View Details
          <ChevronRight size={14} className='ms-1' />
        </button>
      </div>

      <Modal isOpen={modalOpen} toggle={toggle} centered size='lg'>
        <ModalHeader toggle={toggle} className='border-0 pb-0'>
          <span className='d-inline-flex align-items-center'>
            <AlertCircle size={22} className='me-2' style={{ color: '#fd7e14' }} />
            <span style={{ fontWeight: 600 }}>Actions Required</span>
            <span
              className='ms-2'
              style={{
                background: '#fff3cd',
                color: '#664d03',
                fontSize: 12,
                fontWeight: 600,
                padding: '2px 10px',
                borderRadius: 10,
              }}
            >
              {count}
            </span>
          </span>
        </ModalHeader>
        <ModalBody>
          <p className='text-muted mb-3' style={{ fontSize: 13 }}>
            The following items need your attention to keep things running smoothly.
          </p>
          {actions.map((item, idx) => (
            <div
              key={idx}
              className='p-3 mb-2 rounded'
              style={{
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
              }}
            >
              <p className='mb-2 text-dark' style={{ fontSize: 14, lineHeight: 1.5 }}>
                {item.message}
              </p>
              <a
                href={item.url}
                target='_blank'
                rel='noreferrer'
                className='btn btn-sm btn-primary d-inline-flex align-items-center'
                style={{ fontSize: 13 }}
              >
                {item.label}
                <ExternalLink size={12} className='ms-2' />
              </a>
            </div>
          ))}
        </ModalBody>
      </Modal>
    </>
  );
}
